import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { 
  TrendingDown,
  DollarSign,
  Calculator,
  Calendar,
  Search,
  ChevronLeft,
  ChevronRight,
  Download,
  BarChart3,
  PieChart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn, formatCurrency } from '@/lib/utils';
import api from '@/services/api';

interface DepreciationAsset {
  id: string;
  name: string;
  assetTag: string;
  category?: { name: string };
  purchaseDate: string;
  purchaseCost: string;
  currentValue: string;
  depreciationRate: number;
  depreciationMethod: string;
  salvageValue: string;
  usefulLifeMonths: number;
  monthsInService: number;
  totalDepreciation: string;
  monthlyDepreciation: string;
}

interface DepreciationSummary {
  totalPurchaseValue: number;
  totalCurrentValue: number;
  totalDepreciation: number;
  averageAge: number;
  assetCount: number;
}

export default function DepreciationPage() {
  const [search, setSearch] = useState('');
  const [method, setMethod] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['depreciation', { page, search, method, categoryId }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      if (method) params.append('method', method);
      if (categoryId) params.append('categoryId', categoryId);
      const response = await api.get(`/depreciation?${params.toString()}`);
      return response.data;
    },
  });

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await api.get('/categories');
      return response.data.data || [];
    },
  });

  const assets: DepreciationAsset[] = data?.data || [];
  const pagination = data?.pagination;
  const summary: DepreciationSummary = data?.summary || {
    totalPurchaseValue: 0,
    totalCurrentValue: 0,
    totalDepreciation: 0,
    averageAge: 0,
    assetCount: 0
  };

  const depreciationPercentage = summary.totalPurchaseValue > 0 
    ? ((summary.totalDepreciation / summary.totalPurchaseValue) * 100).toFixed(1)
    : '0';

  const handleExport = async () => {
    try {
      const response = await api.get('/depreciation/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `depreciation-report-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Asset Depreciation</h1>
          <p className="text-muted-foreground">Track asset values over time</p>
        </div>
        <Button onClick={handleExport}>
          <Download className="w-4 h-4 mr-2" />
          Export Report
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Purchase Value</p>
                <p className="text-xl font-bold">{formatCurrency(summary.totalPurchaseValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <BarChart3 className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Current Value</p>
                <p className="text-xl font-bold">{formatCurrency(summary.totalCurrentValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                <TrendingDown className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Depreciation</p>
                <p className="text-xl font-bold text-red-600">{formatCurrency(summary.totalDepreciation)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <PieChart className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Depreciation %</p>
                <p className="text-xl font-bold">{depreciationPercentage}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                <Calendar className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg. Asset Age</p>
                <p className="text-xl font-bold">{summary.averageAge.toFixed(1)} months</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Value Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Value Distribution
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Current Value</span>
                <span className="font-medium">{formatCurrency(summary.totalCurrentValue)}</span>
              </div>
              <div className="h-4 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 rounded-full transition-all"
                  style={{ width: `${((summary.totalCurrentValue / summary.totalPurchaseValue) * 100) || 0}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Total Depreciation</span>
                <span className="font-medium text-red-600">{formatCurrency(summary.totalDepreciation)}</span>
              </div>
              <div className="h-4 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-red-500 rounded-full transition-all"
                  style={{ width: `${((summary.totalDepreciation / summary.totalPurchaseValue) * 100) || 0}%` }}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by asset name or tag..."
                className="pl-10"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <select
              className="h-10 px-3 rounded-md border border-input bg-background text-sm"
              value={method}
              onChange={(e) => { setMethod(e.target.value); setPage(1); }}
            >
              <option value="">All Methods</option>
              <option value="STRAIGHT_LINE">Straight Line</option>
              <option value="DECLINING_BALANCE">Declining Balance</option>
              <option value="DOUBLE_DECLINING">Double Declining</option>
              <option value="SUM_OF_YEARS">Sum of Years</option>
            </select>
            <select
              className="h-10 px-3 rounded-md border border-input bg-background text-sm"
              value={categoryId}
              onChange={(e) => { setCategoryId(e.target.value); setPage(1); }}
            >
              <option value="">All Categories</option>
              {(categories || []).map((cat: any) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Assets Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full spinner" />
            </div>
          ) : assets.length === 0 ? (
            <div className="text-center py-12">
              <Calculator className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No depreciation data found</p>
              <p className="text-sm text-muted-foreground mt-1">
                Add assets with purchase cost and date to track depreciation
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Asset</th>
                    <th className="text-left p-4 font-medium">Category</th>
                    <th className="text-right p-4 font-medium">Purchase Cost</th>
                    <th className="text-right p-4 font-medium">Current Value</th>
                    <th className="text-right p-4 font-medium">Depreciation</th>
                    <th className="text-left p-4 font-medium">Method</th>
                    <th className="text-right p-4 font-medium">Monthly Dep.</th>
                    <th className="text-left p-4 font-medium">Age</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {assets.map((asset) => {
                    const depPercent = (parseFloat(asset.totalDepreciation) / parseFloat(asset.purchaseCost)) * 100;
                    return (
                      <tr key={asset.id} className="hover:bg-muted/50">
                        <td className="p-4">
                          <Link to={`/assets/${asset.id}`} className="hover:underline">
                            <p className="font-medium">{asset.name}</p>
                            <p className="text-sm text-muted-foreground font-mono">{asset.assetTag}</p>
                          </Link>
                        </td>
                        <td className="p-4">{asset.category?.name || '-'}</td>
                        <td className="p-4 text-right font-medium">
                          {formatCurrency(parseFloat(asset.purchaseCost))}
                        </td>
                        <td className="p-4 text-right">
                          <span className="font-medium text-green-600">
                            {formatCurrency(parseFloat(asset.currentValue))}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <div>
                            <span className="font-medium text-red-600">
                              -{formatCurrency(parseFloat(asset.totalDepreciation))}
                            </span>
                            <div className="w-16 h-1.5 bg-muted rounded-full mt-1 ml-auto">
                              <div 
                                className="h-full bg-red-500 rounded-full"
                                style={{ width: `${Math.min(100, depPercent)}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="px-2 py-1 text-xs rounded bg-muted">
                            {asset.depreciationMethod.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="p-4 text-right text-sm">
                          {formatCurrency(parseFloat(asset.monthlyDepreciation))}/mo
                        </td>
                        <td className="p-4">
                          <div className="text-sm">
                            <span className="font-medium">{asset.monthsInService}</span>
                            <span className="text-muted-foreground"> / {asset.usefulLifeMonths} mo</span>
                          </div>
                          <div className="w-16 h-1.5 bg-muted rounded-full mt-1">
                            <div 
                              className={cn(
                                "h-full rounded-full",
                                asset.monthsInService / asset.usefulLifeMonths > 0.9 ? "bg-red-500" :
                                asset.monthsInService / asset.usefulLifeMonths > 0.7 ? "bg-yellow-500" : "bg-blue-500"
                              )}
                              style={{ width: `${Math.min(100, (asset.monthsInService / asset.usefulLifeMonths) * 100)}%` }}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {assets.length} of {pagination.total} assets
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" disabled={page === 1} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm">Page {page} of {pagination.totalPages}</span>
              <Button variant="outline" size="icon" disabled={page === pagination.totalPages} onClick={() => setPage(page + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Depreciation Methods Info */}
      <Card>
        <CardHeader>
          <CardTitle>Depreciation Methods</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="p-4 rounded-lg bg-muted/50">
              <h4 className="font-medium mb-1">Straight Line</h4>
              <p className="text-muted-foreground">
                Equal depreciation each period. (Cost - Salvage) / Useful Life
              </p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50">
              <h4 className="font-medium mb-1">Declining Balance</h4>
              <p className="text-muted-foreground">
                Accelerated depreciation based on remaining book value
              </p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50">
              <h4 className="font-medium mb-1">Double Declining Balance</h4>
              <p className="text-muted-foreground">
                2x the straight-line rate applied to declining balance
              </p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50">
              <h4 className="font-medium mb-1">Sum of Years' Digits</h4>
              <p className="text-muted-foreground">
                Accelerated method using fraction of remaining life
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
